import {
  NextNode,
  NodeStatements,
  Outcome,
  OutcomeType,
  FinalNode,
  Questionnaire,
  questionnaireLogicService,
  ruleService,
} from 'utils/adl-services';
import { REASON_SCREENOUT } from 'components/LogicOutcome/constants';

// A screen-out statement carries the screener types. Quota statements, legacy
// allow statements and navigation statements never do.
export const isScreenOutStatement = (statement) =>
  Boolean(statement?.rule) &&
  !statement.rule.quota &&
  statement.outcome?.reason === REASON_SCREENOUT;

export const isQuotaStatement = (statement) => Boolean(statement?.rule?.quota);

export const getNodeStatements = (ql, nodeId) =>
  questionnaireLogicService.findNodeStatementsByNodeIdWithNoErrorThrown(
    ql,
    nodeId,
  )?.statements || [];

export const getScreenOutStatements = (ql, nodeId) =>
  getNodeStatements(ql, nodeId).filter(isScreenOutStatement);

// The leaf conditions of a rule, whatever its nesting.
export const getLeafConditions = (rule) =>
  rule ? ruleService.getConditions(rule) : [];

export const buildScreenOutOutcome = () =>
  new Outcome({
    type: OutcomeType.Failure,
    reason: REASON_SCREENOUT,
    params: new NextNode({ id: FinalNode }),
  });

// Replaces the node's screen-out statements, putting them first (a strict
// selection reports "screenout", not "quota full"). Other statements keep
// their order.
export function qlReplaceScreenOutStatements({
  ql,
  nodeId,
  screenOutStatements,
}) {
  const nodeStatements =
    questionnaireLogicService.findNodeStatementsByNodeIdWithNoErrorThrown(
      ql,
      nodeId,
    );
  if (!nodeStatements) return ql;

  const otherStatements = nodeStatements.statements.filter(
    (statement) => !isScreenOutStatement(statement),
  );

  return questionnaireLogicService.swapNodeStatements(
    ql,
    nodeId,
    new NodeStatements({
      nodeId,
      statements: [...screenOutStatements, ...otherStatements],
    }),
  );
}

const conditionKey = ({ matcher, value }) => `${matcher}:${value}`;

// The statements being replaced, matched to the statements being written so
// re-encoding keeps their ids: by key first, then the left-over ones in order.
export function matchPreviousStatements({ previousStatements, keys, keyOf }) {
  const unused = [...previousStatements];
  const claim = (statement) => {
    if (statement) unused.splice(unused.indexOf(statement), 1);
    return statement;
  };

  return keys
    .map((key) => claim(unused.find((statement) => keyOf(statement) === key)))
    .map((matched) => matched || claim(unused[0]));
}

// Reuses a previous condition with the same matcher and value, so its id stays.
export function makeConditionReuse(previousStatements) {
  const conditions = new Map(
    previousStatements
      .flatMap(({ rule }) => getLeafConditions(rule))
      .map((condition) => [conditionKey(condition), condition]),
  );

  return ({ matcher, value, create }) =>
    conditions.get(conditionKey({ matcher, value })) || create();
}

const swapStatements = (ql, nodeId, statements) =>
  questionnaireLogicService.swapNodeStatements(
    ql,
    nodeId,
    new NodeStatements({ nodeId, statements }),
  );

// The item a quota statement targets: an answer, or a matrix cell.
export const quotaItemOf = ({ rule }) => {
  const value = rule?.conditions?.[0]?.value;
  return value?.row
    ? { answerRefId: value.row, columnRefId: value.column }
    : { answerRefId: value };
};

// The order of a screening node's statements (first match wins): screen-out,
// quotas in answer order, navigation (legacy allow statements), the default.
export function orderScreeningStatements({ statements, answerRefIds = [] }) {
  const rank = (statement) => {
    if (!statement.rule) return 3;
    if (isQuotaStatement(statement)) return 1;
    if (isScreenOutStatement(statement)) return 0;
    return 2;
  };
  const answerIndex = (statement) => {
    const index = answerRefIds.indexOf(quotaItemOf(statement).answerRefId);
    return index === -1 ? answerRefIds.length : index;
  };

  return statements
    .map((statement, position) => ({ statement, position }))
    .sort(
      (a, b) =>
        rank(a.statement) - rank(b.statement) ||
        (rank(a.statement) === 1
          ? answerIndex(a.statement) - answerIndex(b.statement)
          : 0) ||
        a.position - b.position,
    )
    .map(({ statement }) => statement);
}

// Puts a screening node's statements in that order.
export function qlOrderScreeningStatements({ ql, nodeId }) {
  const statements = getNodeStatements(ql, nodeId);
  const { node } = Questionnaire.getNodeInfo(ql.questionnaire, nodeId);
  const ordered = orderScreeningStatements({
    statements,
    answerRefIds: (node?.answers || []).map(({ id }) => id),
  });
  if (ordered.every((statement, index) => statement === statements[index])) {
    return ql;
  }

  return swapStatements(ql, nodeId, ordered);
}

// Removes the node's quota statements that `shouldRemove` accepts.
export function qlRemoveQuotaStatements({ ql, nodeId, shouldRemove }) {
  const statements = getNodeStatements(ql, nodeId);
  const kept = statements.filter(
    (statement) => !(isQuotaStatement(statement) && shouldRemove(statement)),
  );
  if (kept.length === statements.length) return ql;

  return swapStatements(ql, nodeId, kept);
}

// Screen-out, quota and navigation statements: rule statements that pass or
// fail the respondent. Visibility statements have outcome types of their own.
const isPassOrFailStatement = (statement) =>
  Boolean(statement?.rule) &&
  [OutcomeType.Success, OutcomeType.Failure].includes(statement.outcome?.type);

// Puts back the node's pass or fail statements as they were in `previousQl`,
// keeping the node's other statements from `ql`.
export function qlRestorePassOrFailStatements({ ql, previousQl, nodeId }) {
  const kept = getNodeStatements(previousQl, nodeId).filter(
    isPassOrFailStatement,
  );
  const others = getNodeStatements(ql, nodeId).filter(
    (statement) => !isPassOrFailStatement(statement),
  );

  return swapStatements(ql, nodeId, [...kept, ...others]);
}
